import type { Point } from "highcharts";
import styled from "styled-components";

import { colorsPalettes, mixins, rgba } from "@similarweb/styles";
import type { ICategory } from "app/common/services/categoryService.types";
import { chosenSites } from "app/common/services/chosenSites";
import { ClosableItemColorMarker } from "app/components/compare/StyledComponent";
import { CHART_COLORS } from "app/constants/ChartColors";
import { i18nFilter } from "app/filters/i18nFilter";
import { Flex } from "@similarweb/ui-components/foundations";
import { FlexRow, RightFlexRow, FlexColumn } from "pro-features/styled components/StyledFlex/src/StyledFlex";

import { sumObject, getPointY } from "./AllTrafficChannelsOverviewBarChartShared";
import { percentDataFormat, numbersFilter, percentsFilter } from "./ChannelsOverviewBarChartContainerFormat";
import type { TrafficDataByDeviceType, IndustryDataByDeviceType, ILegendItem } from "./types";

const TableRowStyle = styled(FlexRow)`
    font-weight: 400;
    font-style: normal;
    font-size: 14px;
    letter-spacing: 0.6px;
    line-height: 24px;
    justify-content: space-between;
`;

const TableCellDomainMarker = styled(ClosableItemColorMarker)<{ background: string }>`
    position: static;
    background-color: ${(p) => p.background};
    margin: 0 8px 0 0;
    flex-shrink: 0;
    width: 10px;
    height: 10px;
`;

const TableCellDomainName = styled.div`
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 150px;
`;

const TooltipContainer = styled(FlexColumn)`
    padding: 8px 8px 4px 8px;
    max-width: 320px;
    min-width: 320px;
    color: ${rgba(colorsPalettes.carbon[500], 0.8)};
`;

const Title = styled.span`
    ${mixins.setFont({ $size: 16 })};
    font-weight: bold;
    margin-bottom: 10px;
`;

const TableHeadersStyle = styled(Flex)`
    font-size: 12px;
    font-style: normal;
    line-height: 16px;
    font-weight: 700;
    width: 100%;
    justify-content: space-between;
    margin-bottom: 4px;
`;

const TableCellStyle = styled(RightFlexRow)`
    &:first-child {
        text-align: right;
        min-width: 50px;
    }
    &:last-child {
        text-align: right;
        min-width: 50px;
        justify-content: right;
    }
`;

const TableHeaders = ({ headersList }: { headersList: string[] }) => {
    const i18n = i18nFilter();

    return (
        <TableHeadersStyle>
            {headersList.map((header, index) => (
                <TableCellStyle key={index}>{i18n(header)}</TableCellStyle>
            ))}
        </TableHeadersStyle>
    );
};

interface PointData {
    traffic: number;
    color: string;
    name: string;
}

interface TableRowProps {
    point: PointData;
    dataFormat: string;
}

const TableRow = ({ point, dataFormat }: TableRowProps) => {
    const value = point.traffic
        ? dataFormat === percentDataFormat
            ? percentsFilter(point.traffic)
            : numbersFilter(point.traffic)
        : "N/A";

    return (
        <TableRowStyle>
            <TableCellStyle>
                <TableCellDomainMarker background={point.color} />
                <TableCellDomainName>{point.name}</TableCellDomainName>
            </TableCellStyle>
            <TableCellStyle>{value}</TableCellStyle>
        </TableRowStyle>
    );
};

interface IChannelsOverviewBarChartTooltipProps {
    trafficSource: string;
    trafficData: TrafficDataByDeviceType;
    industryData?: IndustryDataByDeviceType;
    points: Point[];
    legends: ILegendItem[];
    dataFormat: string;
    isCompare?: boolean;
    category?: ICategory;
}

export const AllTrafficChannelsOverviewBarChartCompareModeTooltip = ({
    trafficSource,
    trafficData,
    industryData,
    category,
    points,
    legends,
    dataFormat,
    isCompare = true,
}: IChannelsOverviewBarChartTooltipProps) => {
    const pointsList: PointData[] = Object.keys(trafficData.Total ?? {}).map((domain) => {
        const totalTraffic = trafficData.Total?.[domain]?.[trafficSource] ?? 0;
        const totalVisits = sumObject(trafficData.Total?.[domain] ?? {});

        return {
            traffic: getPointY(totalTraffic, totalVisits, dataFormat),
            color: isCompare
                ? chosenSites.getSiteColor(domain)
                : CHART_COLORS.trafficSourcesColorsBySourceMMX[trafficSource],
            name: domain,
        };
    });

    // Add industry data point if industryData is provided
    if (industryData) {
        const industryTotalVisits = sumObject(industryData.Total ?? {});
        const industryTotalTraffic = industryData.Total?.[trafficSource] ?? 0;

        pointsList.push({
            traffic: getPointY(industryTotalTraffic, industryTotalVisits, dataFormat),
            color: colorsPalettes.midnight[50],
            name: category?.text,
        });
    }

    // Filter pointsList based on legends visibility
    const visiblePointsList = pointsList.filter((point) => {
        const correspondingLegend = legends.find((legend) => legend.name === point.name);
        return correspondingLegend?.visible !== false;
    });

    const headersList = [
        "mmx.channelsoverview.bar.chart.tooltip.compare.domain",
        "mmx.channelsoverview.bar.chart.tooltip.compare.TotalTraffic",
    ];

    return (
        <TooltipContainer>
            <Title>{trafficSource}</Title>
            <TableHeaders headersList={headersList} />
            {visiblePointsList.map((pointData) => (
                <TableRow
                    key={pointData.name}
                    dataFormat={dataFormat}
                    point={pointData}
                />
            ))}
        </TooltipContainer>
    );
};
